import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  ArrowLeft,
  QrCode,
  CheckCircle,
  XCircle,
  Users,
  UserCheck,
  UserX,
  Camera,
  CameraOff,
  RefreshCw,
  Banknote,
  CreditCard,
  Clock,
  Search,
  Loader2,
  Volume2,
  VolumeX,
  ImageIcon,
  SwitchCamera
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function EventCheckinPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [event, setEvent] = useState(null);
  const [attendance, setAttendance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [manualTicketId, setManualTicketId] = useState('');
  const [checkingIn, setCheckingIn] = useState(false);
  const [lastCheckin, setLastCheckin] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Camera state
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [useFrontCamera, setUseFrontCamera] = useState(false);
  const [pendingStream, setPendingStream] = useState(null);
  const [lastScannedId, setLastScannedId] = useState(null);
  const [scanPaused, setScanPaused] = useState(false);
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const scanIntervalRef = useRef(null);
  const fileInputRef = useRef(null);

  const getToken = () => localStorage.getItem('shrt_token');

  // Sound effects
  const playSound = (type) => {
    if (!soundEnabled) return;
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      if (type === 'success') {
        oscillator.frequency.setValueAtTime(880, audioContext.currentTime);
        oscillator.frequency.setValueAtTime(1100, audioContext.currentTime + 0.1);
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.3);
      } else if (type === 'error') {
        oscillator.frequency.setValueAtTime(300, audioContext.currentTime);
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.5);
      } else if (type === 'warning') {
        oscillator.frequency.setValueAtTime(600, audioContext.currentTime);
        oscillator.frequency.setValueAtTime(400, audioContext.currentTime + 0.15);
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.3);
      }
    } catch (e) {}
  };

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      return;
    }
    fetchData();
    
    return () => {
      stopCamera();
    };
  }, [id, isAdmin]);

  // Attach stream to video when both are ready
  useEffect(() => {
    if (pendingStream && videoRef.current && cameraActive) {
      const video = videoRef.current;
      console.log('Attaching stream to video element');
      
      video.srcObject = pendingStream;
      
      video.onloadedmetadata = () => {
        console.log('Video metadata loaded, playing...');
        video.play().then(() => {
          console.log('Video playing!');
          startScanning();
        }).catch(err => {
          console.error('Play error:', err);
          startScanning();
        });
      };
      
      setPendingStream(null);
    }
  }, [pendingStream, cameraActive]);

  const fetchData = async () => {
    const token = getToken();
    try {
      const [eventRes, attendanceRes] = await Promise.all([
        axios.get(`${API_URL}/api/events/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/events/${id}/attendance`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setEvent(eventRes.data);
      setAttendance(attendanceRes.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckin = async (registrationId) => {
    if (!registrationId || checkingIn) return;
    
    setCheckingIn(true);
    const token = getToken();
    
    try {
      const response = await axios.post(
        `${API_URL}/api/events/checkin/${registrationId}?event_id=${id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setLastCheckin({
        success: true,
        alreadyCheckedIn: response.data.already_checked_in,
        registration: response.data.registration,
        message: response.data.message
      });
      
      if (response.data.already_checked_in) {
        playSound('warning');
        toast.info(response.data.message);
      } else {
        playSound('success');
        toast.success(response.data.message);
      }
      
      fetchData();
      setManualTicketId('');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Check-in fehlgeschlagen';
      setLastCheckin({ success: false, message: errorMsg });
      playSound('error');
      toast.error(errorMsg);
    } finally {
      setCheckingIn(false);
      setProcessing(false);
    }
  };

  const handleUndoCheckin = async (registrationId) => {
    const token = getToken();
    try {
      await axios.delete(`${API_URL}/api/events/checkin/${registrationId}`, 
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Check-in rückgängig gemacht');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler');
    }
  };

  const extractRegistrationId = (qrContent) => {
    if (qrContent.includes('/checkin/')) {
      const parts = qrContent.split('/checkin/');
      return parts[parts.length - 1];
    }
    return qrContent;
  };

  // Start camera with native API
  const startCamera = async () => {
    setCameraError(null);
    console.log('Starting camera...');
    
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Ihr Browser unterstützt keine Kamera-Funktion.');
      }
      
      // Simple constraints for maximum compatibility
      const constraints = {
        video: useFrontCamera 
          ? { facingMode: 'user' }
          : { facingMode: { ideal: 'environment' } },
        audio: false
      };
      
      console.log('Requesting camera with constraints:', constraints);
      
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia(constraints);
      } catch (e) {
        console.log('Fallback to simple video constraint');
        stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      }
      
      console.log('Got stream:', stream);
      streamRef.current = stream;
      
      // First set camera active to render the video element
      setCameraActive(true);
      // Then set pending stream - useEffect will attach it
      setPendingStream(stream);
      
    } catch (error) {
      console.error('Camera error:', error);
      
      let msg = 'Kamera konnte nicht gestartet werden.';
      if (error.name === 'NotAllowedError') {
        msg = 'Kamera-Zugriff verweigert. Bitte erlauben Sie den Zugriff.';
      } else if (error.name === 'NotFoundError') {
        msg = 'Keine Kamera gefunden.';
      } else if (error.name === 'NotReadableError') {
        msg = 'Kamera wird bereits verwendet.';
      }
      
      setCameraError(msg);
      toast.error('Kamera-Fehler');
    }
  };

  const stopCamera = () => {
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    
    setCameraActive(false);
  };

  const switchCamera = async () => {
    stopCamera();
    setUseFrontCamera(!useFrontCamera);
    setTimeout(() => startCamera(), 100);
  };

  // Scan for QR codes using jsQR
  const startScanning = () => {
    // Load jsQR dynamically
    if (!window.jsQR) {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/jsqr@1.4.0/dist/jsQR.min.js';
      script.onload = () => {
        beginScanLoop();
      };
      document.head.appendChild(script);
    } else {
      beginScanLoop();
    }
  };

  const beginScanLoop = () => {
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
    }
    
    scanIntervalRef.current = setInterval(() => {
      if (!videoRef.current || !canvasRef.current || processing || scanPaused) return;
      
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      
      if (video.readyState !== video.HAVE_ENOUGH_DATA) return;
      
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      
      if (window.jsQR) {
        const code = window.jsQR(imageData.data, imageData.width, imageData.height, {
          inversionAttempts: 'dontInvert'
        });
        
        if (code) {
          const registrationId = extractRegistrationId(code.data);
          
          // Skip if same code was just scanned
          if (registrationId === lastScannedId) {
            return;
          }
          
          // Pause scanning for 4 seconds
          setScanPaused(true);
          setLastScannedId(registrationId);
          setProcessing(true);
          
          handleCheckin(registrationId).then(() => {
            // Resume scanning after 4 seconds
            setTimeout(() => {
              setScanPaused(false);
              setLastScannedId(null);
            }, 4000);
          });
        }
      }
    }, 300);
  };

  // Handle image upload
  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setProcessing(true);
    
    try {
      // Load jsQR if not loaded
      if (!window.jsQR) {
        await new Promise((resolve) => {
          const script = document.createElement('script');
          script.src = 'https://cdn.jsdelivr.net/npm/jsqr@1.4.0/dist/jsQR.min.js';
          script.onload = resolve;
          document.head.appendChild(script);
        });
      }
      
      const img = new Image();
      img.src = URL.createObjectURL(file);
      
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
      });
      
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const code = window.jsQR(imageData.data, imageData.width, imageData.height);
      
      if (code) {
        const registrationId = extractRegistrationId(code.data);
        await handleCheckin(registrationId);
      } else {
        toast.error('Kein QR-Code im Bild gefunden');
        playSound('error');
      }
      
      URL.revokeObjectURL(img.src);
    } catch (error) {
      toast.error('Fehler beim Verarbeiten des Bildes');
      playSound('error');
    } finally {
      setProcessing(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const filterAttendees = (list) => {
    if (!list) return [];
    if (!searchQuery) return list;
    const query = searchQuery.toLowerCase();
    return list.filter(r => 
      r.member_name?.toLowerCase().includes(query) ||
      r.member_email?.toLowerCase().includes(query)
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[50vh]">
          <Loader2 className="animate-spin" size={32} />
        </div>
      </Layout>
    );
  }

  if (!event || !attendance) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">Event nicht gefunden</p>
        </div>
      </Layout>
    );
  }

  const { summary, checked_in, not_checked_in } = attendance;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        {/* Hidden canvas for QR processing */}
        <canvas ref={canvasRef} style={{ display: 'none' }} />
        
        {/* Header */}
        <div className="mb-4">
          <Link to="/admin/checkin" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-2 text-sm">
            <ArrowLeft size={14} />
            Alle Events
          </Link>
          <h1 className="font-['Oswald'] text-xl sm:text-2xl font-bold uppercase tracking-tight">
            Check-in: {event.title}
          </h1>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-2 mb-4">
          <div className="bg-card border border-border p-2 text-center">
            <p className="text-lg font-bold">{summary.total_registrations}</p>
            <p className="text-[9px] text-muted-foreground uppercase">Anmeldungen</p>
          </div>
          <div className="bg-card border border-border p-2 text-center">
            <p className="text-lg font-bold text-green-500">{summary.checked_in_count}</p>
            <p className="text-[9px] text-muted-foreground uppercase">Eingecheckt</p>
          </div>
          <div className="bg-card border border-border p-2 text-center">
            <p className="text-lg font-bold text-yellow-500">{summary.not_checked_in_count}</p>
            <p className="text-[9px] text-muted-foreground uppercase">Ausstehend</p>
          </div>
          <div className="bg-card border border-border p-2 text-center">
            <p className="text-lg font-bold">{summary.checked_in_persons}/{summary.total_expected_persons}</p>
            <p className="text-[9px] text-muted-foreground uppercase">Personen</p>
          </div>
        </div>

        {/* Scanner Section */}
        <div className="bg-card border border-border p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-['Oswald'] text-sm font-bold uppercase tracking-wider flex items-center gap-2">
              <QrCode size={16} className="text-primary" />
              Scanner
            </h2>
            <Button onClick={() => setSoundEnabled(!soundEnabled)} variant="ghost" size="sm" className="h-8 w-8 p-0">
              {soundEnabled ? <Volume2 size={14} /> : <VolumeX size={14} />}
            </Button>
          </div>
          
          {/* Camera View */}
          {cameraActive ? (
            <div className="mb-4">
              {/* Video container - no relative/absolute positioning */}
              <div style={{ 
                maxWidth: '400px', 
                margin: '0 auto',
                backgroundColor: '#000',
                borderRadius: '8px',
                overflow: 'hidden'
              }}>
                <video 
                  ref={videoRef} 
                  autoPlay
                  playsInline
                  muted
                  style={{ 
                    display: 'block',
                    width: '100%', 
                    height: 'auto',
                    minHeight: '300px'
                  }}
                />
              </div>
              <p className="text-xs text-center text-muted-foreground mt-2">QR-Code vor die Kamera halten</p>
              <div className="flex justify-center gap-2 mt-3">
                <Button onClick={switchCamera} variant="outline" size="sm" className="rounded-none">
                  <SwitchCamera size={14} className="mr-1" />
                  Wechseln
                </Button>
                <Button onClick={stopCamera} variant="outline" size="sm" className="rounded-none">
                  <CameraOff size={14} className="mr-1" />
                  Stoppen
                </Button>
              </div>
              {processing && (
                <div className="text-center mt-2">
                  <Loader2 className="animate-spin inline-block" size={24} />
                  <p className="text-sm">Verarbeite...</p>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-2 mb-4">
              <Button onClick={startCamera} className="w-full h-14 rounded-none text-base">
                <Camera size={20} className="mr-2" />
                Kamera starten
              </Button>
              
              <Button 
                onClick={() => fileInputRef.current?.click()}
                variant="outline"
                className="w-full h-10 rounded-none text-sm"
              >
                <ImageIcon size={16} className="mr-2" />
                Bild hochladen
              </Button>
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleImageUpload}
                className="hidden"
              />
              
              {cameraError && (
                <p className="text-destructive text-xs text-center">{cameraError}</p>
              )}
            </div>
          )}
          
          {/* Manual Input */}
          <div className="border-t border-border pt-3 mt-3">
            <p className="text-xs text-muted-foreground mb-2">Ticket-ID manuell:</p>
            <div className="flex gap-2">
              <Input
                value={manualTicketId}
                onChange={(e) => setManualTicketId(e.target.value)}
                placeholder="ID eingeben..."
                className="flex-1 h-10"
                onKeyDown={(e) => e.key === 'Enter' && handleCheckin(manualTicketId.trim())}
              />
              <Button 
                onClick={() => handleCheckin(manualTicketId.trim())}
                disabled={checkingIn || !manualTicketId.trim()}
                className="rounded-none h-10 px-4"
              >
                {checkingIn ? <Loader2 className="animate-spin" size={16} /> : <CheckCircle size={16} />}
              </Button>
            </div>
          </div>
          
          {/* Last Result */}
          {lastCheckin && (
            <div className={`mt-3 p-3 border text-sm ${
              lastCheckin.success 
                ? lastCheckin.alreadyCheckedIn ? 'bg-yellow-500/10 border-yellow-500/30' : 'bg-green-500/10 border-green-500/30'
                : 'bg-red-500/10 border-red-500/30'
            }`}>
              <div className="flex items-center gap-2">
                {lastCheckin.success ? (
                  lastCheckin.alreadyCheckedIn ? <Clock className="text-yellow-500" size={18} /> : <CheckCircle className="text-green-500" size={18} />
                ) : (
                  <XCircle className="text-red-500" size={18} />
                )}
                <div>
                  <p className="font-medium">{lastCheckin.message}</p>
                  {lastCheckin.registration && (
                    <p className="text-xs text-muted-foreground">{lastCheckin.registration.member_name}</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Search */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
          <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Suchen..." className="pl-9 h-9" />
        </div>

        {/* Lists */}
        <div className="space-y-4">
          {/* Not Checked In */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wider mb-2 text-yellow-600 flex items-center gap-1">
              <UserX size={14} />
              Ausstehend ({filterAttendees(not_checked_in).length})
            </h3>
            {filterAttendees(not_checked_in).length > 0 ? (
              <div className="bg-card border border-border divide-y divide-border">
                {filterAttendees(not_checked_in).map(reg => (
                  <div key={reg.id} className="p-2 flex items-center justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1 flex-wrap">
                        <span className="font-medium text-sm">{reg.member_name}</span>
                        {reg.payment_method === 'cash' ? (
                          <span className="px-1 py-0.5 bg-yellow-500/20 text-yellow-700 text-[9px] rounded">Bar</span>
                        ) : (
                          <span className="px-1 py-0.5 bg-green-500/20 text-green-700 text-[9px] rounded">Bezahlt</span>
                        )}
                      </div>
                    </div>
                    <Button onClick={() => handleCheckin(reg.id)} size="sm" className="rounded-none bg-green-600 hover:bg-green-700 h-8 px-3">
                      <CheckCircle size={14} />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-xs">Alle eingecheckt! 🎉</p>
            )}
          </div>

          {/* Checked In */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wider mb-2 text-green-600 flex items-center gap-1">
              <UserCheck size={14} />
              Eingecheckt ({filterAttendees(checked_in).length})
            </h3>
            {filterAttendees(checked_in).length > 0 ? (
              <div className="bg-card border border-border divide-y divide-border">
                {filterAttendees(checked_in).map(reg => (
                  <div key={reg.id} className="p-2 flex items-center justify-between gap-2">
                    <div className="flex-1 min-w-0 flex items-center gap-1">
                      <CheckCircle size={12} className="text-green-500" />
                      <span className="font-medium text-sm">{reg.member_name}</span>
                      <span className="text-[10px] text-muted-foreground">
                        {new Date(reg.checked_in_at).toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <Button onClick={() => handleUndoCheckin(reg.id)} size="sm" variant="ghost" className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive">
                      <RefreshCw size={12} />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-xs">Noch niemand</p>
            )}
          </div>
        </div>

        <div className="mt-4 text-center">
          <Button onClick={fetchData} variant="outline" size="sm" className="rounded-none text-xs">
            <RefreshCw size={12} className="mr-1" />
            Aktualisieren
          </Button>
        </div>
      </div>
    </Layout>
  );
}
